import { Component, computed, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BaseModalComponent } from '@card-software/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { Roll } from '@app/models/knitting/roll';
import { ProductionCardsProxyService } from '@app/proxies/production/production-card.proxy.service';
import { IndividualProductionCardService } from '@app/services/individual-production-card.service';

export type ReturnRollsToGreigeModalResult = string[];

@Component({
  selector: 'app-return-rolls-to-greige-modal',
  templateUrl: './return-rolls-to-greige-modal.component.html',
  styleUrl: './return-rolls-to-greige-modal.component.scss',
})
export class ReturnRollsToGreigeModalComponent extends BaseModalComponent<ReturnRollsToGreigeModalResult | null> {
  private readonly individualProductionCardService = inject(IndividualProductionCardService);
  private readonly productionCardProxyService = inject(ProductionCardsProxyService);
  private readonly translate = inject(TranslateService);

  isLoading = signal<number>(0);
  selectedRollsToReturn = signal<Roll[]>([]);
  returnRollFilter = signal<string>('');

  rollsAvailableForReturn = computed<Roll[]>(() => {
    const bundles = this.individualProductionCardService.productionCardBundles;
    const bundledRolls = new Set<string>();
    bundles.forEach((bundle) => bundle.rollNumbers.forEach((rn) => bundledRolls.add(rn)));

    const availableRolls = this.individualProductionCardService.availableRolls;
    const pc = this.individualProductionCardService.productionCard();

    const rollNumbersInTransactions = new Set(
      this.individualProductionCardService
        .activeInventoryTransactionsInBatchingSignal()
        .flatMap((tx) => (tx.lines || []).map((line) => line.lotItemKey.number)),
    );

    return availableRolls.filter(
      (roll) =>
        rollNumbersInTransactions.has(roll.number!) &&
        !bundledRolls.has(roll.number!) &&
        roll.currentlyLinkedToProductionCardKey?.reference === pc?.id,
    );
  });

  filteredRollsAvailableForReturn = computed<Roll[]>(() => {
    const filter = this.returnRollFilter().toLowerCase().trim();
    if (!filter) return this.rollsAvailableForReturn();
    return this.rollsAvailableForReturn().filter((roll) => roll.number?.toLowerCase().includes(filter));
  });

  onReturnSelected(): void {
    const rollsToReturn = this.selectedRollsToReturn();
    if (rollsToReturn.length === 0) {
      GlobalMessageUtil.warn(
        this.translate.instant('Common_NoSelection'),
        this.translate.instant('Production_Card_Move_To_Batch_SelectRollsToReturn'),
      );
      return;
    }

    const pc = this.individualProductionCardService.getProductionCardOrThrow();
    const rollNumbers = rollsToReturn.map((r) => r.number!);

    this.isLoading.update((n) => n + 1);
    this.productionCardProxyService.returnRollsToGreige(pc.id!, rollNumbers).subscribe({
      next: (response) => {
        this.isLoading.update((n) => n - 1);
        if (response.success) {
          GlobalMessageUtil.success(
            this.translate.instant('Production_Card_Move_To_Batch_ReturnRollsToGreige'),
            `${rollNumbers.length} roll(s) returned to Greige`,
          );
          this.close({ action: 'save', data: rollNumbers });
        } else {
          GlobalMessageUtil.error(this.translate.instant('Common_Error'), this.translate.instant('Common_OperationFailed'));
        }
      },
      error: () => {
        this.isLoading.update((n) => n - 1);
      },
    });
  }

  onCancel(): void {
    this.close({ action: 'close', data: null });
  }
}
